import { html, TemplateResult } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { classMap } from "lit/directives/class-map.js";
import { DodonaElement } from "@/components/meta/dodona_element";
import { I18n } from "@/i18n/i18n";
import { SubmissionStatus } from "@/submission_status";

/**
 * Icon, label and title of the hand-in card header.
 * Names the exercise while the editor is open and the submission being viewed otherwise,
 * with the exercise's icon throughout. d-handin-card sets `submission` when it switches
 * between the two.
 * Submissions without a number predate numbering and show the exercise name instead.
 *
 * Styles: app/assets/stylesheets/components/card.css.scss (.card-eyebrow),
 * app/assets/stylesheets/components/handin.css.scss (.celebrating)
 *
 * @element d-handin-title
 *
 * @prop {string} exerciseName - Name of the exercise the card hands in.
 * @prop {string} exerciseIcon - Icon of the exercise, without the `mdi-` prefix.
 * @prop {SubmissionStatus|null} submission - Submission being shown, or null while editing.
 */
@customElement("d-handin-title")
export class HandinTitle extends DodonaElement {
    @property({ type: String, attribute: "exercise-name" })
    exerciseName = "";

    @property({ type: String, attribute: "exercise-icon" })
    exerciseIcon = "";

    @property({ type: Object })
    submission: SubmissionStatus | null = null;

    @state()
    private celebration: string | null = null;

    private celebrationTimeout: ReturnType<typeof setTimeout> | undefined;

    /**
     * Shows `submission` with `icon` instead of its status icon for 4 seconds, after it was judged.
     * Sets both at once, so the status icon is not shown briefly before the celebration.
     * A new celebration replaces the running one, so its timer cannot cut the new one short.
     */
    celebrate(submission: SubmissionStatus, icon: string): void {
        this.submission = submission;
        this.celebration = icon;
        clearTimeout(this.celebrationTimeout);
        this.celebrationTimeout = setTimeout(() => {
            this.celebration = null;
        }, 4000);
    }

    disconnectedCallback(): void {
        clearTimeout(this.celebrationTimeout);
        super.disconnectedCallback();
    }

    private get number(): number | null {
        return this.submission?.number ?? null;
    }

    private get icon(): string {
        return this.celebration ?? `mdi-${this.exerciseIcon}`;
    }

    // i18n-tasks-use t('js.handin.eyebrow_editor') t('js.handin.eyebrow_result')
    render(): TemplateResult {
        return html`
            <span
                class=${classMap({ "card-eyebrow-icon": true, celebrating: this.celebration !== null })}
                aria-hidden="true"
            >
                <i class="mdi mdi-24 ${this.icon}"></i>
            </span>

            <div class="card-eyebrow-text">
                <span class="card-eyebrow-label"
                    >${I18n.t(`js.handin.eyebrow_${this.submission ? "result" : "editor"}`)}</span
                >

                <h2 class="card-eyebrow-title">
                    ${
                        this.number === null
                            ? this.exerciseName
                            : I18n.t("js.handin.title_result", { number: this.number })
                    }
                </h2>
            </div>
        `;
    }
}
